import React, { useEffect, useState } from "react";
import { Award, ArrowRight, Trophy } from "lucide-react";
import { dashProgressDetail, openApp, errorMessage } from "./api";
import { Modal } from "./ui";
import { ProgressRing as Ring, GradientBar, useProgressGo, PROGRESS_CSS, PROGRESS_TONE as TONE, TILE_TONES, PROGRESS_INK as INK, progressTone as toneOf } from "./ProgressCards";

const TILE_ORDER = [TILE_TONES.done, TILE_TONES.poems, TILE_TONES.other, TILE_TONES.pending];

// What the card behind a "Learning progress" tile opens: the same layout for every app, showing only the parts
// that app actually has (stat tiles, leaderboard rank, badges, a breakdown). Loaded on open, not with the Overview.

const MESSAGES = {
  not_started: "You haven't started this app yet. Open it to begin, and your progress will appear here.",
  unavailable: "Couldn't load these details right now. Please try again in a moment.",
  not_connected: "Detailed progress isn't available for this account.",
};

function Rank({ rank }) {
  const { position, of, scope } = rank;
  return (
    <div className="pc-tile rounded-2xl p-3 flex items-center gap-3 h-full" style={{ background: TILE_TONES.pending.tint }} data-testid="detail-rank">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: `linear-gradient(135deg, ${TILE_TONES.pending.a}, ${TILE_TONES.pending.b})` }}><Trophy className="h-[18px] w-[18px]" /></span>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Leaderboard</div>
        {position
          ? <div className="font-semibold text-forest-700">#{position} <span className="font-normal text-foreground/70">of {of} · {scope}</span></div>
          : <div className="text-sm text-foreground/70">Not in the {scope.toLowerCase()} yet</div>}
      </div>
    </div>
  );
}

function Badges({ badges }) {
  const n = badges.earned.length;
  return (
    <section data-testid="detail-badges">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display font-bold text-forest-700">Badges</h3>
        <span className="text-xs text-foreground/60">{badges.total ? `${n} of ${badges.total} earned` : `${n} earned`}</span>
      </div>
      {n === 0
        ? <p className="mt-2 text-sm text-foreground/60">No badges earned yet.</p>
        : <ul className="mt-2 flex flex-wrap gap-2">
            {badges.earned.map((b) => (
              <li key={b.id} className="inline-flex items-center gap-1.5 rounded-full text-white text-xs font-semibold px-3 py-1.5 pc-pop" style={{ background: `linear-gradient(135deg, ${TONE.mid.a}, ${TONE.mid.b})` }}>
                <Award className="h-3.5 w-3.5" />{b.name}
              </li>))}
          </ul>}
    </section>
  );
}

export default function ProgressDetailModal({ item, app, token, onClose }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    dashProgressDetail(token, item.product)
      .then((res) => alive && setState({ res }))
      .catch((err) => alive && setState({ error: errorMessage(err, MESSAGES.unavailable) }));
    return () => { alive = false; };
  }, [token, item.product]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const go = useProgressGo();
  const tone = toneOf(Number(item.percent) || 0);
  const { loading, res, error } = state;
  const d = res?.detail;
  const canOpen = app && app.has_access && app.available && app.url;

  return (
    <Modal title={item.name} onClose={onClose} wide>
      <div data-testid={`progress-detail-${item.product}`} className="space-y-5">
        <style>{PROGRESS_CSS}</style>
        <div className="relative overflow-hidden rounded-2xl p-4 flex items-center gap-4" style={{ background: tone.tint }}>
          <span className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${tone.a}, ${tone.b})` }} />
          <Ring pct={Number(item.percent) || 0} tone={tone} go={go} size={88} decimals={0} />
          <div className="min-w-0"><div className="text-sm text-foreground/80">{item.summary}</div></div>
        </div>

        {loading && <p className="text-sm text-foreground/60" data-testid="detail-loading">Loading details…</p>}
        {error && <p className="text-sm text-red-700" data-testid="detail-error">{error}</p>}
        {res && !d && <p className="text-sm text-foreground/70" data-testid="detail-empty">{MESSAGES[res.status] || MESSAGES.unavailable}</p>}

        {d && (<>
          {(d.stats?.length > 0 || d.rank) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {d.stats.map((s, i) => {
                const t = TILE_ORDER[i % TILE_ORDER.length];
                return (
                  <div key={s.label} className="pc-tile pc-pop rounded-2xl px-3 py-3" style={{ background: t.tint, animationDelay: `${i * 70}ms` }}>
                    <div className="text-[11px] uppercase tracking-wider text-foreground/60">{s.label}</div>
                    <div className="mt-1 font-display font-bold text-xl leading-tight" style={{ color: t.text }}>{s.value}</div>
                  </div>);
              })}
              {d.rank && <div className="col-span-2">{<Rank rank={d.rank} />}</div>}
            </div>)}
          {d.badges && <Badges badges={d.badges} />}
          {d.rows?.length > 0 && (
            <section data-testid="detail-rows">
              <h3 className="font-display font-bold text-forest-700">Breakdown</h3>
              <ul className="mt-3 space-y-2.5">
                {d.rows.map((r, i) => (
                  <li key={`${r.label}-${i}`} className="pc-rise rounded-2xl border border-border bg-white px-4 py-3" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                    <div className="flex items-baseline justify-between gap-3 text-sm"><span>{r.label}</span><span className="font-display font-bold text-right" style={{ color: INK }}>{r.value}</span></div>
                    {r.percent != null && <div className="mt-2"><GradientBar pct={Number(r.percent) || 0} go={go} delay={200 + Math.min(i, 8) * 60} h={8} /></div>}
                  </li>))}
              </ul>
            </section>)}
        </>)}

        {canOpen && (
          <div className="pt-1">
            <button type="button" data-testid="detail-open-app" onClick={() => openApp(app, token)} className="btn-primary">
              Open {item.name} <ArrowRight className="h-4 w-4" />
            </button>
          </div>)}
      </div>
    </Modal>
  );
}
